import { z } from 'zod';

import { entityIdSchema, isoDateTimeSchema } from '../../../../core/primitives/schema';

/**
 * Medical declaration — canonical schema per §6.1.
 *
 * Split across two documents on purpose (ADR-004 data minimisation):
 *
 * - `declarations/{id}` — status, ownership, timestamps, and **non-sensitive**
 *   answers. This is what the committee lists and filters.
 * - `declarations/{id}/medical/{policyYear}` — the **sensitive** answers.
 *   Readable only by roles passing `canReadMedicalDetail`.
 *
 * The split means the committee's most frequent operation — listing and
 * triaging declarations — never reads anyone's health information, and finance
 * cannot reach it at all.
 */

export const declarationStatusSchema = z.enum([
  /** Employee is still editing. The only status an employee may write. */
  'draft',
  'submitted',
  'under_review',
  'needs_documents',
  'approved',
  'rejected',
]);

/**
 * Statuses an employee may still edit from. Once submitted, changes go through
 * the committee — otherwise an employee could alter a declaration after review.
 */
export const EMPLOYEE_EDITABLE_STATUSES: readonly DeclarationStatus[] = ['draft', 'needs_documents'];

/** Answer value. The permitted shape depends on the item's declared type. */
export const answerValueSchema = z.union([
  z.boolean(),
  z.string().max(2000),
  z.number(),
  z.array(z.string().max(200)).max(50),
]);

export const declarationAnswerSchema = z.object({
  itemId: entityIdSchema,
  value: answerValueSchema,
});

export const declarationSchema = z.object({
  schemaVersion: z.literal(1),

  id: entityIdSchema,
  /** Owner. Set server-side from the verified identity, never client-claimed. */
  employeeId: entityIdSchema,
  /** Which policy version these answers were given under (§6.2). */
  policyYear: z.string().trim().regex(/^\d{4}-\d{4}$/),

  status: declarationStatusSchema,

  /** Non-sensitive answers only. Sensitive ones live in the subcollection. */
  answers: z.array(declarationAnswerSchema).max(100),

  submittedAt: isoDateTimeSchema.optional(),
  reviewedAt: isoDateTimeSchema.optional(),
  /** Reviewer's identity, derived server-side (§4.2). */
  reviewedBy: entityIdSchema.optional(),
  /** Shown to the employee when more documents are needed or on rejection. */
  reviewNoteAr: z.string().trim().max(1000).optional(),

  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

/** Sensitive answers. Separate document, separate read authorization. */
export const declarationMedicalSchema = z.object({
  schemaVersion: z.literal(1),
  declarationId: entityIdSchema,
  employeeId: entityIdSchema,
  answers: z.array(declarationAnswerSchema).max(100),
  updatedAt: isoDateTimeSchema,
});

/**
 * What an employee may submit.
 *
 * `status`, `employeeId`, `reviewedBy`, and every timestamp are absent: an
 * employee cannot approve their own declaration, cannot claim it belongs to
 * someone else, and cannot forge a review. Omission at the boundary beats
 * stripping fields afterwards, because there is no field to forget to strip.
 */
export const declarationDraftSchema = declarationSchema.pick({
  answers: true,
});

export type DeclarationStatus = z.infer<typeof declarationStatusSchema>;
export type DeclarationAnswer = z.infer<typeof declarationAnswerSchema>;
export type Declaration = z.infer<typeof declarationSchema>;
export type DeclarationMedical = z.infer<typeof declarationMedicalSchema>;
export type DeclarationDraft = z.infer<typeof declarationDraftSchema>;
